import React, { useState } from 'react';
import { useCourseSkillList } from '@/hooks/queries/skill/useList';
import { useCourseSkillCreate, useCourseSkillUpdate, useCourseSkillDelete } from '@/hooks/mutations/skill';
import { CourseSkill, CourseSkillInput } from '@/types/courseSkills';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Sparkles } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const CourseSkillsPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<CourseSkill | null>(null);

  const [formData, setFormData] = useState<CourseSkillInput>({
    icon: '',
    courseId: '',
    translations: [
      { locale: 'uz', name: '' },
      { locale: 'ru', name: '' },
      { locale: 'en', name: '' },
    ],
  });

  const { data: skillsData, isLoading, refetch } = useCourseSkillList({
    pageNumber: page,
    pageSize: 50,
  });

  const { mutate: createSkill, isPending: isCreating } = useCourseSkillCreate();
  const { mutate: updateSkill, isPending: isUpdating } = useCourseSkillUpdate();
  const { mutate: deleteSkill } = useCourseSkillDelete();

  const handleOpenCreate = () => {
    setSelectedSkill(null);
    setFormData({
      icon: '',
      courseId: '',
      translations: [
        { locale: 'uz', name: '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (skill: CourseSkill) => {
    setSelectedSkill(skill);
    setFormData({
      icon: skill.icon || '',
      courseId: skill.courseId || '',
      translations: [
        { locale: 'uz', name: skill.name || '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, name: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSkill) {
      updateSkill(
        { id: selectedSkill.id, data: { icon: formData.icon, translations: formData.translations } },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: "Ko'nikma yangilandi" });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: "Ko'nikma yangilanmadi", variant: 'destructive' }),
        }
      );
    } else {
      createSkill(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: "Yangi ko'nikma yaratildi" });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: "Ko'nikma yaratilmadi", variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedSkill) return;
    deleteSkill(selectedSkill.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Ko'nikma o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedSkill(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Ko'nikma o'chirilmadi", variant: 'destructive' }),
    });
  };

  const skillsList: CourseSkill[] = skillsData?.data?.data || skillsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kurs Ko'nikmalari (Skills)</h1>
          <p className="text-sm text-slate-500 mt-1">Talabalar o'rganadigan asosiy ko'nikmalar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi ko'nikma
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : skillsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Sparkles className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha ko'nikmalar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Ikonka</th>
                  <th className="px-4 py-3.5">Ko'nikma Nomi</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {skillsList.map((skill) => (
                  <tr key={skill.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {skill.icon ? (
                        <img src={getMediaUrl(skill.icon)} alt={skill.name} className="size-8 object-contain rounded border border-slate-100 p-1 bg-white" />
                      ) : (
                        <div className="size-8 rounded bg-blue-50 text-primary flex items-center justify-center font-bold">
                          <Sparkles className="size-4" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{skill.name || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(skill)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedSkill(skill);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={skillsData?.meta?.pagination ?? skillsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedSkill ? "Ko'nikmani tahrirlash" : "Yangi ko'nikma yaratish"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <FileUploadInput
              label="Ikonka (Rasm)"
              value={typeof formData.icon === 'string' ? formData.icon : ''}
              onChange={(url) => setFormData({ ...formData, icon: url })}
            />

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { name: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Ko'nikma nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.name || ''}
                          onChange={(e) => handleTranslationChange(locale, e.target.value)}
                          placeholder="TypeScript"
                          className="rounded-xl"
                          required={locale === 'uz'}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Ko'nikmani o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu ko'nikma o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default CourseSkillsPage;
